import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import {
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  IconButton,
  Box,
  Switch,
  FormControlLabel,
  FormGroup,
  Checkbox,
  ListItemText,
  Grid,
  Paper,
} from "@material-ui/core";
import {
  Visibility,
  VisibilityOff,
  DeleteOutline,
  Add,
} from "@material-ui/icons";
import { InputAdornment } from "@material-ui/core";
import { allowedModels, getModelLabel } from "../../constants/aiModels";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  section: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  optionCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  optionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
}));

const emptyOption = () => ({
  id: `opt_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
  label: "",
  description: "",
  tagName: "",
  allowedContactTagIds: [],
  allowedContactTagNames: [],
  allowedKanbanTagIds: [],
  allowedKanbanTagNames: [],
});

const FlowBuilderAddOpenAIAgentModal = ({
  open,
  onSave,
  data,
  onUpdate,
  close,
}) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const initialState = {
    label: "Roteador por IA",
    prompt: "",
    model: "gpt-3.5-turbo-1106",
    apiKey: "",
    maxMessages: 10,
    allowContactTagActions: false,
    allowKanbanChange: false,
    enableRag: false,
    documentationIds: [],
    documentationNames: [],
    ragTopK: 4,
    ragMinScore: 0.25,
    arrayOption: [],
  };

  const [showApiKey, setShowApiKey] = useState(false);
  const [form, setForm] = useState(initialState);
  const [contactTagOptions, setContactTagOptions] = useState([]);
  const [kanbanOptions, setKanbanOptions] = useState([]);
  const [docOptions, setDocOptions] = useState([]);
  const [labels, setLabels] = useState({
    title: "Editar Roteador por IA",
    btn: "Salvar",
  });

  useEffect(() => {
    if (open !== "edit" && open !== "create") return;

    api
      .get(`/tags/list`, { params: { kanban: 0 } })
      .then(({ data: tagsData }) => {
        const tags = Array.isArray(tagsData) ? tagsData : [];
        setContactTagOptions(
          tags.map((tag) => ({ id: tag.id, name: tag.name }))
        );
      })
      .catch(() => setContactTagOptions([]));

    api
      .get(`/tag/kanban`)
      .then(({ data: kanbanData }) => {
        const lanes = Array.isArray(kanbanData?.lista)
          ? kanbanData.lista
          : Array.isArray(kanbanData)
            ? kanbanData
            : [];
        setKanbanOptions(lanes.map((tag) => ({ id: tag.id, name: tag.name })));
      })
      .catch(() => setKanbanOptions([]));

    api
      .get(`/internal-documents/ready`)
      .then(({ data: docsData }) => {
        const docs = Array.isArray(docsData?.records)
          ? docsData.records
          : Array.isArray(docsData)
            ? docsData
            : [];
        setDocOptions(docs.map((d) => ({ id: d.id, name: d.title })));
      })
      .catch(() => setDocOptions([]));
  }, [open]);

  useEffect(() => {
    if (open === "edit") {
      setLabels({ title: "Editar Roteador por IA", btn: "Salvar" });
      const nodeData = data?.data || {};
      const integration = nodeData.typebotIntegration || {};
      const options = Array.isArray(nodeData.arrayOption)
        ? nodeData.arrayOption.map((opt) => ({
            ...emptyOption(),
            ...opt,
            allowedContactTagIds: Array.isArray(opt.allowedContactTagIds)
              ? opt.allowedContactTagIds
              : [],
            allowedKanbanTagIds: Array.isArray(opt.allowedKanbanTagIds)
              ? opt.allowedKanbanTagIds
              : [],
          }))
        : [];

      setForm({
        ...initialState,
        label: nodeData.label || integration.name || "Roteador por IA",
        prompt: integration.prompt || "",
        model: allowedModels.includes(integration.model)
          ? integration.model
          : "gpt-3.5-turbo-1106",
        apiKey: integration.apiKey || "",
        maxMessages: integration.maxMessages || 10,
        allowContactTagActions: !!(
          integration.allowContactTagActions ?? nodeData.allowContactTagActions
        ),
        allowKanbanChange: !!(
          integration.allowKanbanChange ?? nodeData.allowKanbanChange
        ),
        enableRag: !!(integration.enableRag ?? nodeData.enableRag),
        documentationIds: Array.isArray(
          integration.documentationIds || nodeData.documentationIds
        )
          ? integration.documentationIds || nodeData.documentationIds
          : [],
        ragTopK: integration.ragTopK ?? nodeData.ragTopK ?? 4,
        ragMinScore: integration.ragMinScore ?? nodeData.ragMinScore ?? 0.25,
        arrayOption: options,
      });
    } else if (open === "create") {
      setLabels({ title: "Adicionar Roteador por IA", btn: "Adicionar" });
      setForm({
        ...initialState,
        arrayOption: [emptyOption()],
      });
    }

    return () => {
      isMounted.current = false;
    };
  }, [open, data]);

  const handleClose = () => {
    close(null);
  };

  const setField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const resolveNames = (ids, options) =>
    options
      .filter((opt) => (ids || []).includes(opt.id))
      .map((opt) => opt.name);

  const patchOption = (index, patch) =>
    setForm((prev) => {
      const arrayOption = [...(prev.arrayOption || [])];
      arrayOption[index] = { ...arrayOption[index], ...patch };
      return { ...prev, arrayOption };
    });

  const renderMultiSelect = ({ label, value, options, onChange }) => (
    <FormControl fullWidth margin="dense" variant="outlined">
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        label={label}
        value={value || []}
        onChange={(e) => onChange(e.target.value)}
        renderValue={(selected) =>
          options
            .filter((t) => selected.includes(t.id))
            .map((t) => t.name)
            .join(", ")
        }
      >
        {options.map((opt) => (
          <MenuItem key={opt.id} value={opt.id}>
            <Checkbox
              color="primary"
              checked={(value || []).includes(opt.id)}
            />
            <ListItemText primary={opt.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const handleSave = () => {
    const documentationIds = form.documentationIds || [];
    const arrayOption = (form.arrayOption || []).map((opt) => {
      const allowedContactTagIds = opt.allowedContactTagIds || [];
      const allowedKanbanTagIds = opt.allowedKanbanTagIds || [];
      return {
        id: opt.id || emptyOption().id,
        label: opt.label || "",
        description: opt.description || "",
        tagName: opt.tagName || "",
        targetNodeId: opt.targetNodeId,
        allowedContactTagIds,
        allowedContactTagNames: resolveNames(
          allowedContactTagIds,
          contactTagOptions
        ),
        allowedKanbanTagIds,
        allowedKanbanTagNames: resolveNames(allowedKanbanTagIds, kanbanOptions),
      };
    });

    const typebotIntegration = {
      name: form.label || "Roteador por IA",
      prompt: form.prompt || "",
      model: form.model,
      apiKey: form.apiKey || "",
      maxMessages: Number(form.maxMessages) || 10,
      allowContactTagActions: !!form.allowContactTagActions,
      allowKanbanChange: !!form.allowKanbanChange,
      enableRag: !!form.enableRag,
      documentationIds,
      documentationNames: resolveNames(documentationIds, docOptions),
      ragTopK: Number(form.ragTopK) || 4,
      ragMinScore:
        form.ragMinScore === "" || form.ragMinScore === undefined
          ? 0.25
          : Number(form.ragMinScore),
    };

    const payloadData = {
      label: form.label || "Roteador por IA",
      typebotIntegration,
      arrayOption,
      allowContactTagActions: !!form.allowContactTagActions,
      allowKanbanChange: !!form.allowKanbanChange,
      enableRag: !!form.enableRag,
      documentationIds,
      documentationNames: typebotIntegration.documentationNames,
      ragTopK: typebotIntegration.ragTopK,
      ragMinScore: typebotIntegration.ragMinScore,
    };

    if (open === "edit") {
      onUpdate({
        ...data,
        data: {
          ...(data?.data || {}),
          ...payloadData,
        },
      });
    } else if (open === "create" && onSave) {
      onSave(payloadData);
    }
    handleClose();
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open === "create" || open === "edit"}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <DialogTitle>{labels.title}</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Nome / label do nó"
            variant="outlined"
            margin="dense"
            fullWidth
            value={form.label}
            onChange={(e) => setField("label", e.target.value)}
          />
          <TextField
            label="API Key"
            type={showApiKey ? "text" : "password"}
            variant="outlined"
            margin="dense"
            fullWidth
            value={form.apiKey}
            onChange={(e) => setField("apiKey", e.target.value)}
            helperText="Deixe em branco para usar a credencial padrão das Configurações"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowApiKey(!showApiKey)}>
                    {showApiKey ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Prompt / instruções do roteador"
            variant="outlined"
            margin="dense"
            fullWidth
            multiline
            rows={6}
            value={form.prompt}
            onChange={(e) => setField("prompt", e.target.value)}
          />
          <Grid container spacing={2}>
            <Grid item xs={8}>
              <FormControl fullWidth margin="dense" variant="outlined">
                <InputLabel>Modelo</InputLabel>
                <Select
                  label="Modelo"
                  value={form.model}
                  onChange={(e) => setField("model", e.target.value)}
                >
                  {allowedModels.map((model) => (
                    <MenuItem key={model} value={model}>
                      {getModelLabel(model)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Máx. mensagens"
                type="number"
                variant="outlined"
                margin="dense"
                fullWidth
                value={form.maxMessages}
                onChange={(e) =>
                  setField("maxMessages", Number(e.target.value))
                }
                inputProps={{ min: 1, max: 50 }}
              />
            </Grid>
          </Grid>

          <Box className={classes.section}>
            <Typography variant="subtitle1">Ações no CRM</Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={!!form.allowContactTagActions}
                    onChange={(e) =>
                      setField("allowContactTagActions", e.target.checked)
                    }
                  />
                }
                label="Permitir aplicar tags no contato"
              />
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={!!form.allowKanbanChange}
                    onChange={(e) =>
                      setField("allowKanbanChange", e.target.checked)
                    }
                  />
                }
                label="Permitir mudar etapa do Kanban"
              />
            </FormGroup>
          </Box>

          <Box className={classes.section}>
            <Typography variant="subtitle1">
              Documentação interna (RAG)
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={!!form.enableRag}
                  onChange={(e) => setField("enableRag", e.target.checked)}
                />
              }
              label="Habilitar RAG"
            />
            {form.enableRag && (
              <>
                {renderMultiSelect({
                  label: "Documentações",
                  value: form.documentationIds,
                  options: docOptions,
                  onChange: (ids) => setField("documentationIds", ids),
                })}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="Top K"
                      type="number"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      value={form.ragTopK}
                      onChange={(e) =>
                        setField("ragTopK", Number(e.target.value))
                      }
                      inputProps={{ min: 1, max: 20 }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Score mínimo"
                      type="number"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      value={form.ragMinScore}
                      onChange={(e) =>
                        setField("ragMinScore", Number(e.target.value))
                      }
                      inputProps={{ min: 0, max: 1, step: 0.05 }}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </Box>

          <Box className={classes.section}>
            <div className={classes.optionHeader}>
              <div>
                <Typography variant="subtitle1">Opções de roteamento</Typography>
                <Typography variant="caption" color="textSecondary">
                  Cada opção vira uma saída do nó. Tags por opção restringem ações
                  CRM naquela fase.
                </Typography>
              </div>
              <Button
                color="primary"
                variant="outlined"
                startIcon={<Add />}
                onClick={() =>
                  setForm((prev) => ({
                    ...prev,
                    arrayOption: [...(prev.arrayOption || []), emptyOption()],
                  }))
                }
              >
                Adicionar opção
              </Button>
            </div>

            {(form.arrayOption || []).map((opt, index) => (
              <Paper key={opt.id} className={classes.optionCard} elevation={0}>
                <div className={classes.optionHeader}>
                  <Typography variant="subtitle2">
                    Opção #{index + 1}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        arrayOption: (prev.arrayOption || []).filter(
                          (_, i) => i !== index
                        ),
                      }))
                    }
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </div>
                <Grid container spacing={1}>
                  <Grid item xs={4}>
                    <TextField
                      label="ID"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      value={opt.id}
                      onChange={(e) => patchOption(index, { id: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={8}>
                    <TextField
                      label="Label"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      value={opt.label}
                      onChange={(e) =>
                        patchOption(index, { label: e.target.value })
                      }
                    />
                  </Grid>
                </Grid>
                <TextField
                  label="Descrição (para a IA)"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  multiline
                  rows={2}
                  value={opt.description}
                  onChange={(e) =>
                    patchOption(index, { description: e.target.value })
                  }
                />
                <TextField
                  label="Tag Kanban legada (nome)"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  value={opt.tagName || ""}
                  onChange={(e) =>
                    patchOption(index, { tagName: e.target.value })
                  }
                  helperText="Compatibilidade com fluxos SDR antigos"
                />
                {renderMultiSelect({
                  label: "Tags de contato da opção",
                  value: opt.allowedContactTagIds,
                  options: contactTagOptions,
                  onChange: (ids) =>
                    patchOption(index, { allowedContactTagIds: ids }),
                })}
                {renderMultiSelect({
                  label: "Etapas Kanban da opção",
                  value: opt.allowedKanbanTagIds,
                  options: kanbanOptions,
                  onChange: (ids) =>
                    patchOption(index, { allowedKanbanTagIds: ids }),
                })}
              </Paper>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            className={classes.btnWrapper}
          >
            {labels.btn}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FlowBuilderAddOpenAIAgentModal;
